import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdPersonAdd, MdRefresh } from 'react-icons/md'
import { useUser } from '../context/UserContext'
import { listarSyncEjecHist, syncEjecHist } from '../api/apiConfig'

const pick = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k]
    if (v != null && String(v).trim() !== '') return v
  }
  return ''
}

const unwrapFilas = (response) => {
  if (Array.isArray(response?.filas)) return response.filas
  if (Array.isArray(response)) return response
  return []
}

const LABEL = {
  ausente: 'No está en ejec_hist',
  inactivo: 'Está inactivo',
  sin_mail: 'Sin correo en ejec_hist',
  revisar_equipo: 'Equipo distinto',
  ok: 'Sincronizado',
}

const BADGE = {
  ausente: 'bg-red-100 text-red-800',
  inactivo: 'bg-orange-100 text-orange-800',
  sin_mail: 'bg-amber-100 text-amber-800',
  revisar_equipo: 'bg-yellow-100 text-yellow-800',
  ok: 'bg-green-100 text-green-800',
}

const SyncEjecHist = () => {
  const { user } = useUser()
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [soloPendientes, setSoloPendientes] = useState(true)

  const applyResponse = (resp) => {
    setFilas(unwrapFilas(resp))
  }

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setMessage('')
    try {
      const resp = await listarSyncEjecHist(user)
      applyResponse(resp)
    } catch (err) {
      setFilas([])
      setMessage(err?.message || 'No se pudo cargar el desfase')
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const visibles = useMemo(() => {
    return filas.filter((r) => {
      const estado = pick(r, 'ESTADO_SYNC', 'estado_sync')
      if (soloPendientes && estado === 'ok') return false
      return true
    })
  }, [filas, soloPendientes])

  const pendientes = useMemo(
    () => filas.filter((r) => r.ACCIONABLE || r.accionable),
    [filas],
  )

  const handleSync = async (fila) => {
    const clave = pick(fila, 'EJEC_SIST_FACTORING', 'ejec_sis_fact')
    setSaving(clave)
    setMessage('')
    try {
      const resp = await syncEjecHist(user, { op: 'sync', ejec_sis_fact: clave })
      applyResponse(resp)
      const resultado = resp?.resultados?.[0]
      if (resultado?.success) {
        setMessage(`${clave} ${resultado.accion} en ejec_hist. En la próxima corrida de bolsa (08:00 / 10:00 / 12:30 / 15:00) deberían entrar sus facturas.`)
        setMessageType('ok')
      } else {
        setMessage(resultado?.message || 'No se pudo sincronizar')
        setMessageType('error')
      }
    } catch (err) {
      setMessage(err?.message || 'No se pudo sincronizar')
      setMessageType('error')
    } finally {
      setSaving('')
    }
  }

  const handleSyncAll = async () => {
    setSaving('__all__')
    setMessage('')
    try {
      const resp = await syncEjecHist(user, { op: 'sync_all' })
      applyResponse(resp)
      const ok = (resp?.resultados || []).filter((r) => r.success).length
      const fail = (resp?.resultados || []).filter((r) => !r.success).length
      setMessage(`Sincronizados: ${ok}. Con error: ${fail}.`)
      setMessageType(fail ? 'error' : 'ok')
    } catch (err) {
      setMessage(err?.message || 'No se pudo sincronizar el lote')
      setMessageType('error')
    } finally {
      setSaving('')
    }
  }

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Sincronizar notificaciones de facturas</h1>
          <p className="text-gray-600 text-sm mt-1 max-w-3xl">
            El correo y la bolsa de documentos leen <code className="text-xs bg-gray-100 px-1 rounded">COMERCIAL.ejec_hist</code>.
            El alta de usuarios escribe en <code className="text-xs bg-gray-100 px-1 rounded">MGC.ejecutivos_estado</code>.
            Aquí ves quién quedó fuera y lo agregas con un clic.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <MdRefresh className="w-4 h-4" />
            Recargar
          </button>
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={loading || saving !== '' || pendientes.length === 0}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            <MdPersonAdd className="w-4 h-4" />
            Agregar todos los pendientes ({pendientes.length})
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          checked={soloPendientes}
          onChange={(e) => setSoloPendientes(e.target.checked)}
        />
        Mostrar solo pendientes o con diferencias
      </label>

      {message && (
        <div
          className={`mb-4 p-3 rounded text-sm ${
            messageType === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          }`}
        >
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <p className="text-gray-500">Cargando desfase...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mail MGC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre en factoring</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Equipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acción</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-sm text-gray-500 text-center">
                      No hay ejecutivos en este filtro.
                    </td>
                  </tr>
                )}
                {visibles.map((fila) => {
                  const estado = pick(fila, 'ESTADO_SYNC', 'estado_sync')
                  const clave = pick(fila, 'EJEC_SIST_FACTORING')
                  const accionable = Boolean(fila.ACCIONABLE ?? fila.accionable)
                  return (
                    <tr key={clave || pick(fila, 'MAIL')}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{pick(fila, 'NOMBRE')}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pick(fila, 'MAIL')}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{clave}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {pick(fila, 'GRUPO')} → {pick(fila, 'EQUIPO_OBJETIVO')}
                        {pick(fila, 'EQUIPO_HIST') ? ` (hoy: ${pick(fila, 'EQUIPO_HIST')})` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${BADGE[estado] || 'bg-gray-100 text-gray-700'}`}>
                          {LABEL[estado] || estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {accionable ? (
                          <button
                            type="button"
                            onClick={() => handleSync(fila)}
                            disabled={saving !== ''}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                          >
                            <MdPersonAdd className="w-3.5 h-3.5" />
                            {saving === clave ? 'Agregando...' : 'Agregar a ejec_hist'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SyncEjecHist
